/**
 * Runtime registration of the right-click row actions on the embedded DLQ dashboard:
 * "5. Show in Investigations Pane" (investigations drawer) and "6. Go to Investigation (new tab)".
 *
 * The QuickSight embedding SDK (≥ 2.3.0) lets the host page add a custom action to a visual at
 * runtime, so the right-click menu entry ships with the frontend instead of being hand-added to
 * every QuickSight analysis (design doc §7.1). Selecting the entry emits
 * `CALLBACK_OPERATION_INVOKED` with the clicked action's id and the row's columns. "5." opens the
 * drawer seeded from the row (`parseQuickSightCallback`); "6." opens the investigation named by the
 * row's `investigation_id` column, or the drawer when the row has none.
 *
 * QuickSight custom actions are visual-wide, not row-wide. To avoid showing "6." on a row that
 * cannot use it, this module only registers that action when either:
 * - the visual title explicitly marks it as an investigated-only split visual; or
 * - the sheet's investigation-state filter limits the whole visual to investigated rows.
 *
 * A mixed table keeps "5." but does not show "6.". See `rowVisualInvestigationScope` and
 * `investigationScopeFromFilterGroups`.
 */

import { parseQuickSightCallbackValues } from "../investigations/investigationUtils";

/** Minimal shapes of the QuickSight embedding SDK objects we touch. */
export interface QsVisual {
  Name?: string;
  VisualId: string;
}
export interface QsVisualAction {
  CustomActionId?: string;
  Name?: string;
  Status?: "ENABLED" | "DISABLED";
  Trigger?: "DATA_POINT_CLICK" | "DATA_POINT_MENU";
  ActionOperations?: Array<Record<string, unknown>>;
}
export interface QsFilterGroup {
  Filters?: Array<{
    CategoryFilter?: {
      Column?: { ColumnName?: string };
      Configuration?: Record<string, unknown>;
    };
  }>;
}
/** Subset of `DashboardExperience` needed to register the action (kept narrow for tests). */
export interface RowActionRegistrar {
  getSelectedSheetId: () => Promise<string>;
  getSheetVisuals: (sheetId: string) => Promise<QsVisual[]>;
  getVisualActions: (
    sheetId: string,
    visualId: string
  ) => Promise<QsVisualAction[]>;
  addVisualActions: (
    sheetId: string,
    visualId: string,
    actions: QsVisualAction[]
  ) => Promise<{ success: boolean; errorCode?: string; error?: string }>;
  removeVisualActions: (
    sheetId: string,
    visualId: string,
    actions: QsVisualAction[]
  ) => Promise<{ success: boolean; errorCode?: string; error?: string }>;
  getFilterGroupsForSheet: (sheetId: string) => Promise<QsFilterGroup[]>;
}

export const INVESTIGATE_ACTION_ID = "lusca-investigate-in-lusca";
/** Numbered to sit after the dashboard's own "1.–4." URL actions in the context menu. */
export const INVESTIGATE_ACTION_NAME = "5. Show in Investigations Pane";
/** Earlier names of this action (hand-added per the old runbook, or a previous frontend build). */
const LEGACY_ACTION_NAMES = [
  "investigate in lusca",
  "5. open investigations",
  "5. open investigations pane",
];

export const GO_TO_INVESTIGATION_ACTION_ID = "lusca-go-to-investigation";
export const GO_TO_INVESTIGATION_ACTION_NAME =
  "6. Go to Investigation (new tab)";
const LEGACY_GO_TO_NAMES = ["6. go to investigation"];

/** The context-menu callback action registered on the DLQ row visual(s). */
export function buildInvestigateAction(): QsVisualAction {
  return {
    CustomActionId: INVESTIGATE_ACTION_ID,
    Name: INVESTIGATE_ACTION_NAME,
    Status: "ENABLED",
    Trigger: "DATA_POINT_MENU",
    ActionOperations: [{ CallbackOperation: { EmbeddingMessage: {} } }],
  };
}

/** The context-menu callback action that jumps straight to the row's investigation. */
export function buildGoToInvestigationAction(): QsVisualAction {
  return {
    CustomActionId: GO_TO_INVESTIGATION_ACTION_ID,
    Name: GO_TO_INVESTIGATION_ACTION_NAME,
    Status: "ENABLED",
    Trigger: "DATA_POINT_MENU",
    ActionOperations: [{ CallbackOperation: { EmbeddingMessage: {} } }],
  };
}

/** The CustomActionId of the menu entry a CALLBACK_OPERATION_INVOKED message came from, or "". */
export function callbackActionId(message: unknown): string {
  const id = (message as { CustomActionId?: unknown })?.CustomActionId;
  return typeof id === "string" ? id : "";
}

/**
 * Visual titles that identify the row-level DLQ table. The dashboards are not source-controlled,
 * so this is a heuristic; when nothing matches we fall back to every visual on the sheet (a
 * callback from a non-row visual carries no `dlq_pk` and opens the manual drawer instead).
 */
const ROW_VISUAL_PATTERNS: RegExp[] = [
  // Title of the row table on the Gamma/Prod DLQ dashboards (seen 2026-09-23).
  /transaction\s*breakdown/i,
  /dlq.*\b(items?|rows?|events?|details?|messages?|records?)\b/i,
  /\b(items?|rows?|events?|details?|messages?|records?)\b.*dlq/i,
  /^dlq\s*(data|table|list)$/i,
  /dlq[_\s]?pk/i,
];

/**
 * Titles that look like a row table by the patterns above but are aggregates (KPIs, charts).
 * QuickSight rejects callback actions on KPI visuals with INTERNAL_ERROR, e.g. "DLQ Item Count".
 */
const NON_ROW_VISUAL_PATTERNS: RegExp[] = [
  /\b(counts?|totals?|kpi|trend|over time|per (day|hour|week))\b/i,
];

export interface RowVisualSelection {
  visuals: QsVisual[];
  /** False when no title matched and every visual on the sheet was selected instead. */
  matchedByName: boolean;
}

export function selectRowVisuals(visuals: QsVisual[]): RowVisualSelection {
  const matched = visuals.filter((v) => {
    const name = v.Name || "";
    return (
      ROW_VISUAL_PATTERNS.some((p) => p.test(name)) &&
      !NON_ROW_VISUAL_PATTERNS.some((p) => p.test(name))
    );
  });
  if (matched.length) return { visuals: matched, matchedByName: true };
  return { visuals, matchedByName: false };
}

/** True when the visual already offers "6. Go to investigation" (ours, or one added by hand). */
export function hasGoToInvestigationAction(actions: QsVisualAction[]): boolean {
  const wanted = [
    GO_TO_INVESTIGATION_ACTION_NAME.toLowerCase(),
    ...LEGACY_GO_TO_NAMES,
  ];
  return actions.some(
    (a) =>
      a.CustomActionId === GO_TO_INVESTIGATION_ACTION_ID ||
      wanted.includes((a.Name || "").trim().toLowerCase())
  );
}

/** The matching "6." actions, including an older action added by hand in QuickSight. */
export function goToInvestigationActions(
  actions: QsVisualAction[]
): QsVisualAction[] {
  const wanted = [
    GO_TO_INVESTIGATION_ACTION_NAME.toLowerCase(),
    ...LEGACY_GO_TO_NAMES,
  ];
  return actions.filter(
    (a) =>
      a.CustomActionId === GO_TO_INVESTIGATION_ACTION_ID ||
      wanted.includes((a.Name || "").trim().toLowerCase())
  );
}

/**
 * True when the visual already offers the action — either ours from an earlier registration or
 * one added by hand in the analysis per the runbook — so we never show two menu entries.
 */
export function hasInvestigateAction(actions: QsVisualAction[]): boolean {
  const wanted = [
    INVESTIGATE_ACTION_NAME.toLowerCase(),
    ...LEGACY_ACTION_NAMES,
  ];
  return actions.some(
    (a) =>
      a.CustomActionId === INVESTIGATE_ACTION_ID ||
      wanted.includes((a.Name || "").trim().toLowerCase())
  );
}

/** Our row actions this visual does not offer yet, in menu order. */
export function missingRowActions(
  actions: QsVisualAction[],
  includeGoTo = true
): QsVisualAction[] {
  const missing: QsVisualAction[] = [];
  if (!hasInvestigateAction(actions)) missing.push(buildInvestigateAction());
  if (includeGoTo && !hasGoToInvestigationAction(actions))
    missing.push(buildGoToInvestigationAction());
  return missing;
}

export type InvestigationScope = "investigated" | "uninvestigated" | "mixed";

const normalise = (value?: string) =>
  (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const INVESTIGATION_STATE_COLUMNS = new Set([
  "investigationstate",
  "investigation",
  "investigated",
]);

const categoryValues = (
  configuration?: Record<string, unknown>
): string[] | null => {
  if (!configuration) return null;
  const list = configuration.FilterListConfiguration as
    | {
        CategoryValues?: unknown[];
        SelectAllOptions?: string;
      }
    | undefined;
  if (list?.SelectAllOptions === "FILTER_ALL_VALUES") return null;
  const values = list?.CategoryValues;
  if (Array.isArray(values)) {
    return values.filter((value): value is string => typeof value === "string");
  }
  const custom = configuration.CustomFilterConfiguration as
    | { CategoryValue?: unknown; MatchOperator?: string }
    | undefined;
  if (
    custom?.MatchOperator === "EQUALS" &&
    typeof custom.CategoryValue === "string"
  ) {
    return [custom.CategoryValue];
  }
  return null;
};

/**
 * Whether the sheet-level filter guarantees that every displayed row is investigated.
 *
 * Unknown/all-values filters are treated as mixed. This is intentionally conservative: a false
 * negative hides a shortcut, while a false positive exposes it on an ineligible row.
 */
export function investigationScopeFromFilterGroups(
  groups: QsFilterGroup[]
): InvestigationScope {
  let sawInvestigatedOnly = false;
  let sawUninvestigatedOnly = false;
  groups.forEach((group) => {
    (group.Filters || []).forEach((filter) => {
      const category = filter.CategoryFilter;
      if (
        INVESTIGATION_STATE_COLUMNS.has(normalise(category?.Column?.ColumnName))
      ) {
        const values = categoryValues(category?.Configuration)?.map(normalise);
        const hasInvestigated = values?.includes("investigated") ?? false;
        const hasUninvestigated = values?.includes("uninvestigated") ?? false;
        if (hasInvestigated && !hasUninvestigated) sawInvestigatedOnly = true;
        if (hasUninvestigated && !hasInvestigated) sawUninvestigatedOnly = true;
      }
    });
  });
  if (sawInvestigatedOnly && !sawUninvestigatedOnly) return "investigated";
  if (sawUninvestigatedOnly && !sawInvestigatedOnly) return "uninvestigated";
  return "mixed";
}

/**
 * Dashboard-authoring convention for the exact solution:
 * - `Transaction Breakdown — Investigated`
 * - `Transaction Breakdown — Uninvestigated`
 *
 * Those visuals can coexist on one sheet or live on separate sheets.
 */
export function rowVisualInvestigationScope(
  visual: QsVisual
): InvestigationScope {
  const name = visual.Name || "";
  const hasUninvestigated =
    /\buninvestigated\b/i.test(name) ||
    /\bnot(?:\s+yet)?\s+investigated\b/i.test(name);
  const withoutUninvestigated = name
    .replace(/\buninvestigated\b/gi, "")
    .replace(/\bnot(?:\s+yet)?\s+investigated\b/gi, "");
  const hasInvestigated =
    /\binvestigated(?:\s+only)?\b/i.test(withoutUninvestigated) ||
    /\bunder\s+investigation\b/i.test(withoutUninvestigated);
  if (hasUninvestigated && !hasInvestigated) return "uninvestigated";
  if (hasInvestigated && !hasUninvestigated) return "investigated";
  return "mixed";
}

export interface RowActionRegistration {
  sheetId: string;
  matchedByName: boolean;
  filterScope: InvestigationScope;
  /** Visual names at least one action was added to during this call. */
  registered: string[];
  /** Visual names the ineligible "6." action was removed from. */
  removed: string[];
  /** Visual names that already had every action. */
  skipped: string[];
  failed: Array<{ visual: string; error: string }>;
  elapsedMs: number;
}

const visualLabel = (v: QsVisual) => v.Name || v.VisualId;

/**
 * Registers the action on the row visual(s) of one sheet. Per-visual failures are collected
 * rather than thrown so one bad visual never blocks the others; only sheet-level lookups throw.
 */
export async function registerInvestigateAction(
  experience: RowActionRegistrar,
  sheetId?: string
): Promise<RowActionRegistration> {
  const startedAt = Date.now();
  const targetSheet = sheetId ?? (await experience.getSelectedSheetId());
  const [visuals, filterGroups] = await Promise.all([
    experience.getSheetVisuals(targetSheet),
    experience.getFilterGroupsForSheet(targetSheet).catch((err) => {
      console.warn(
        "Could not read the investigation-state filter; hiding action 6:",
        err
      );
      return [];
    }),
  ]);
  const selection = selectRowVisuals(visuals);
  const filterScope = investigationScopeFromFilterGroups(filterGroups);
  const result: RowActionRegistration = {
    sheetId: targetSheet,
    matchedByName: selection.matchedByName,
    filterScope,
    registered: [],
    removed: [],
    skipped: [],
    failed: [],
    elapsedMs: 0,
  };
  const registerOne = async (visual: QsVisual): Promise<void> => {
    const label = visualLabel(visual);
    try {
      const existing = await experience.getVisualActions(
        targetSheet,
        visual.VisualId
      );
      const missing = missingRowActions(existing || []);
      if (!missing.length) {
        result.skipped.push(label);
        return;
      }
      if (missing.length) {
        const response = await experience.addVisualActions(
          targetSheet,
          visual.VisualId,
          missing
        );
        if (response && response.success === false) {
          throw new Error(
            response.error || response.errorCode || "unknown add error"
          );
        }
        result.registered.push(label);
      }
    } catch (err) {
      result.failed.push({
        visual: label,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  await Promise.all(selection.visuals.map(registerOne));
  result.elapsedMs = Date.now() - startedAt;
  return result;
}

/**
 * The investigation a clicked row already belongs to, from the dashboard's `investigation_id`
 * column (PVAccounting projection join, design doc §7.2), or null when the row has none or the
 * visual does not carry the column. Only `inv-` ids are accepted so a blank/placeholder cell
 * ("", "null", "-") falls through to the drawer instead of opening a broken page.
 */
export function parseQuickSightCallbackInvestigationId(
  message: unknown
): string | null {
  const id = (
    parseQuickSightCallbackValues(message).investigation_id || ""
  ).trim();
  return /^inv-[A-Za-z0-9_-]+$/.test(id) ? id : null;
}
